import React from 'react';

const MusicControlModal = ({
  isOpen,
  onClose,
  musicManager,
  isMobile = false
}) => {
  if (!isOpen) return null;

  const {
    isMusicEnabled,
    toggleMusic,
    currentTrack,
    changeTrack,
    volume,
    changeVolume,
    isContinuousPlay,
    toggleContinuousPlay,
    nextTrack,
    previousTrack,
    tracks,
    isLoading,
    audioError
  } = musicManager;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`relative bg-gradient-to-br from-purple-900/95 to-blue-900/95 rounded-2xl shadow-2xl border border-purple-400/30 backdrop-blur-md ${
          isMobile ? 'max-w-sm w-full' : 'max-w-md w-full'
        } max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              🎵 Music Controls
            </h2>
            <button
              onClick={onClose}
              className="text-gray-300 hover:text-white transition-colors text-2xl"
            >
              ✕
            </button>
          </div>

          {audioError && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-400/30 rounded-lg">
              <p className="text-red-300 text-sm">⚠️ Audio playback error. Please try again.</p>
            </div>
          )}

          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg">
              <span className="text-white font-semibold">Music</span>
              <button
                onClick={toggleMusic}
                className={`relative w-14 h-8 rounded-full transition-colors duration-200 ${
                  isMusicEnabled ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 ${
                    isMusicEnabled ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-white font-semibold">Volume</span>
                <span className="text-gray-300 text-sm">{Math.round(volume * 100)}%</span>
              </div>
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume * 100}
                  onChange={(e) => changeVolume(e.target.value / 100)}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  disabled={!isMusicEnabled}
                />
                <style jsx>{`
                  .slider::-webkit-slider-thumb {
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    background: ${isMusicEnabled ? '#10b981' : '#6b7280'};
                    cursor: pointer;
                    border-radius: 50%;
                    transition: background-color 0.2s;
                  }
                  .slider::-webkit-slider-thumb:hover {
                    background: ${isMusicEnabled ? '#059669' : '#4b5563'};
                  }
                  .slider::-moz-range-thumb {
                    width: 20px;
                    height: 20px;
                    background: ${isMusicEnabled ? '#10b981' : '#6b7280'};
                    cursor: pointer;
                    border-radius: 50%;
                    border: none;
                    transition: background-color 0.2s;
                  }
                  .slider::-moz-range-thumb:hover {
                    background: ${isMusicEnabled ? '#059669' : '#4b5563'};
                  }
                `}</style>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg">
              <span className="text-white font-semibold">Keep Playing</span>
              <button
                onClick={toggleContinuousPlay}
                disabled={!isMusicEnabled}
                className={`relative w-14 h-8 rounded-full transition-colors duration-200 ${
                  isContinuousPlay ? 'bg-blue-500' : 'bg-gray-600'
                } ${!isMusicEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 ${
                    isContinuousPlay ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-white font-semibold">
                  {isContinuousPlay ? 'Playlist' : 'Track Selection'}
                </span>
                {isLoading && (
                  <span className="text-yellow-300 text-sm animate-pulse">Loading...</span>
                )}
              </div>

              {isContinuousPlay && isMusicEnabled && (
                <div className="flex items-center justify-center gap-4 p-3 bg-white/5 rounded-lg">
                  <button
                    onClick={previousTrack}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                    title="Previous track"
                  >
                    ⏮️
                  </button>
                  <div className="text-white text-center">
                    <div className="text-sm opacity-70">Now Playing</div>
                    <div className="font-semibold">{currentTrack.name}</div>
                  </div>
                  <button
                    onClick={nextTrack}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                    title="Next track"
                  >
                    ⏭️
                  </button>
                </div>
              )}

              <div className="space-y-2">
                {tracks.map((track) => (
                  <button
                    key={track.id}
                    onClick={() => changeTrack(track)}
                    disabled={!isMusicEnabled}
                    className={`w-full p-3 rounded-lg text-left transition-all duration-200 flex items-center justify-between ${
                      currentTrack.id === track.id
                        ? 'bg-gradient-to-r from-green-600/40 to-blue-600/40 border border-green-400/50'
                        : 'bg-white/10 hover:bg-white/20 border border-transparent'
                    } ${!isMusicEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">
                        {currentTrack.id === track.id ? '▶️' : '🎵'}
                      </span>
                      <span className="text-white font-medium">{track.name}</span>
                    </div>
                    {currentTrack.id === track.id && (
                      <span className="text-green-300 text-sm">
                        {isContinuousPlay ? 'Playing' : 'Now Playing'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 p-4 bg-white/5 rounded-lg space-y-2">
              <p className="text-gray-300 text-sm">
                💡 <span className="font-semibold">Tip:</span> {isContinuousPlay
                  ? 'Songs will automatically play one after another!'
                  : 'Enable keep playing to hear all tracks in row!'}
              </p>
              <p className="text-gray-300 text-xs opacity-70">
                Your preferences are saved automatically
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicControlModal;